import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap'
import Select from 'react-select'
import { recipeAPI } from '../api/recipeAPI'
import { brandAPI } from '../api/brandAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'
import ConfirmationModal from '../components/ConfirmationModal'
import StockItemInfo from '../components/StockItemInfo'
import CopyableText from '../components/CopyableText'
import '../styles/selectControl.css'

function RecipeListScreen() {
    const { hasPermission } = usePermissions()
    const [recipes, setRecipes] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [selectedBrand, setSelectedBrand] = useState('')
    const [brandMap, setBrandMap] = useState({})
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState(null)

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('recipeColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            recipe_id: 'auto',
            image: 70,
            gtin: 'auto',
            item_number: 'auto',
            name: 'auto',
            brand: 'auto',
            output_stock_item_name: 'auto',
            ingredients_count: 'auto',
            actions: 'auto'
        };
    });

    useEffect(() => {
        localStorage.setItem('recipeColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchBrands() {
            try {
                const response = await brandAPI.getAll(1, 1000, '', false)
                const brands = response.results || []
                const mapping = brands.reduce((acc, brand) => {
                    acc[brand._id] = brand.name
                    return acc
                }, {})
                setBrandMap(mapping)
            } catch (error) {
                console.error('Error fetching brands:', error)
            }
        }
        fetchBrands()
    }, [])

    useEffect(() => {
        async function fetchRecipes() {
            try {
                setLoading(true)
                const response = await recipeAPI.getAll(currentPage, pageSize, searchTerm, showDeactivated, selectedBrand || undefined)
                setRecipes(response.results)
                setTotalPages(response.count)
            } finally {
                setLoading(false)
            }
        }
        fetchRecipes()
    }, [currentPage, pageSize, searchTerm, showDeactivated, selectedBrand])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const formatBrandOptions = () => {
        return Object.entries(brandMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const handleBrandFilterChange = (selectedOption) => {
        setSelectedBrand(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const handleDeleteClick = (e, recipe) => {
        e.stopPropagation();
        setItemToDelete(recipe);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                await recipeAPI.delete(itemToDelete._id);
                setRecipes(recipes.filter(r => r._id !== itemToDelete._id));
            } catch (error) {
                console.error('Error deleting recipe:', error);
                alert(`Failed to delete recipe: ${error.response?.data?.message || error.message}`);
            } finally {
                setShowDeleteModal(false);
                setItemToDelete(null);
            }
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Recipes</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search by ID, name, item #, GTIN..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button
                        variant={showDeactivated ? "secondary" : "outline-secondary"}
                        className="me-2"
                        onClick={() => {
                            setShowDeactivated(!showDeactivated);
                            setCurrentPage(1);
                        }}
                    >
                        {showDeactivated ? "Hide Deactivated" : "Show Deactivated"}
                    </Button>
                    {hasPermission('recipes.modify') && (
                        <Button
                            onClick={() => navigate('/recipe/create')}
                            variant="success"
                        >
                            Create Recipe
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="recipes_table"
                        columns={[
                            {
                                id: 'recipe_id',
                                header: 'ID',
                                cell: (row) => highlightText(String(row._id), searchTerm)
                            },
                            {
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.output_stock_item_data?.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.output_stock_item_data} />
                                            </Tooltip>
                                        }
                                    >
                                        <img
                                            src={row.output_stock_item_data.image}
                                            alt="Output Product"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            },
                            {
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => <CopyableText value={row.output_stock_item_data?.gtin} toastLabel="GTIN" searchTerm={searchTerm} />
                            },
                            {
                                id: 'item_number',
                                header: 'Item #',
                                cell: (row) => row.item_number ? <CopyableText value={row.item_number} toastLabel="Item Number" searchTerm={searchTerm} /> : ''
                            },
                            {
                                id: 'brand',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedBrand ? { value: selectedBrand, label: brandMap[selectedBrand] } : null}
                                            onChange={handleBrandFilterChange}
                                            options={formatBrandOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Brand"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => row.output_stock_item_data?.brand_name || ''
                            },
                            {
                                id: 'output_stock_item_name',
                                header: 'Output Product',
                                cell: (row) => highlightText(row.output_stock_item_data?.description || '-', searchTerm)
                            },
                            {
                                id: 'ingredients_count',
                                header: 'Ingredients',
                                cell: (row) => row.ingredients_count
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <div style={{ display: 'flex', gap: '4px', whiteSpace: 'nowrap' }}>
                                        {hasPermission('production.create') && (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/recipe/${row._id}/execute`)
                                                }}
                                            >
                                                Execute
                                            </Button>
                                        )}
                                        {hasPermission('recipes.modify') && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={(e) => handleDeleteClick(e, row)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                        data={recipes}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/recipe/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the recipe "${itemToDelete?.name}"? All ingredients will also be deleted.`}
            />
        </Container>
    )
}

export default RecipeListScreen
